import * as React from 'react';
import { Alert, Button, Grid } from '../common';
import RequestFormWrapper from '../common/wrappers/RequestFormWrapper';
import UserLookup from '../common/form-controls/Lookups/UserLookup';
import TextAreaField from '../common/form-controls/TextareaField';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import AlertModal from '../common/modals/AlertModal';
import ConfirmModal from '../common/modals/ConfirmModal';
import { RuleOperation } from '../../models/enums/POR/RuleOperation';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	AbstractAWSPORForm,
	AbstractAWSPORFormProps,
	AbstractAWSPORFormState,
	IAWSPORStateForm,
} from './components/AbstractAWSPORForm';
import { ApproverInput } from '../PortOpeningRequests/components';
import AWSAccount from '../../models/entities/AWSAccount';
import AWSPortOpeningRequest from '../../models/entities/AWSPortOpeningRequests';
import AWSPortOpeningRule from '../../models/entities/AWSPortOpeningRule';
import { VirtualPrivateCloud } from '../../models/entities/AzureResources/VirtualPrivateCloud';
import AWSAccountLookup from '../common/form-controls/Lookups/AWSAccountLookup';
import { AWSRulesTable } from './components/AWSRulesTable';
import AWSPortOpeningRequestsService from '../../services/AWSPortOpeningRequestsService';
import SubmitAWSPortOpeningRequest from '../../models/viewModels/PortOpeningRequests/SubmitAWSPortOpeningRequest';
import SubmitAWSPortOpeningRequestRule from '../../models/viewModels/PortOpeningRequests/SubmitAWSPortOpeningRequestRule';
import { EnvironmentInput } from './components/EnvironmentInput';
import CartaInput from '../common/form-controls/Lookups/CartaInput';
import AWSPORTypeInput from './components/AWSPORTypeInput';
import { AWSPORScopeInput } from './components/AWSPORScopeInput';
import { AWSPortOpeningRequestsTypes } from '../../models/enums/POR/AWSPortOpeningRequestType';
import SelectField from '../common/form-controls/SelectField';
import { TextLink } from '../common/components/TextLinkNew';

interface AWSPortOpeningRequestsCreateProps extends AWSPortOpeningRequestsCreateWithParamsProps {}

interface AWSPortOpeningRequestsCreateWithParamsProps extends AbstractAWSPORFormProps {
	accountId?: string;
	secondaryAccountId?: string;
	draftRequest?: AWSPortOpeningRequest;
	validNets?: VirtualPrivateCloud[];
}

interface StateForm extends IAWSPORStateForm {
	selectedScope: string;
	selectedType: string;
}

interface ILocationState {
	selectedRules?: AWSPortOpeningRule[];
}

interface AWSPortOpeningRequestsCreateWithParamsState extends AbstractAWSPORFormState<StateForm> {
	awsAccount: AWSAccount | null;
	secondaryAwsAccount: AWSAccount | null;

	cancelModalVisible: boolean;
	changeAccountModalVisible: boolean;
	changeSecondaryAccountModalVisible: boolean;
	changeScopeModalVisible: boolean;
	changeTypeModalVisible: boolean;
	newScope: string;
	newType: string;
	newAccount: AWSAccount;
	newSecondaryAccount: AWSAccount;
	implementedRules: AWSPortOpeningRule[];
	virtualPrivateClouds: VirtualPrivateCloud[];
	secondaryVirtualPrivateClouds: VirtualPrivateCloud[];
	loading: boolean;
	subToInternetWarningVisible: boolean;
	loadingImplementedRules: boolean;
	failedImplementedRules: boolean;
	selectedFromExisting: string[];
}

class AWSPortOpeningRequestsCreateWithParams extends AbstractAWSPORForm<
	AWSPortOpeningRequestsCreateWithParamsProps,
	AWSPortOpeningRequestsCreateWithParamsState
> {
	awsAccountService = new AWSPortOpeningRequestsService();

	constructor(props) {
		super(props);
		this.state = {
			awsAccount: null,
			secondaryAwsAccount: null,
			stateForm: {
				selectedType: '',
				selectedScope: '',
				cartaId: [],
				contact: null,
				customerCoordination: null,
				businessJustification: '',
				ruleList: [],
			},
			endModal: {
				visible: false,
			},
			submissionAlert: {
				visible: false,
			},
			submissionWarned: false,
			operationInProgress: false,
			cancelModalVisible: false,
			changeAccountModalVisible: false,
			changeSecondaryAccountModalVisible: false,
			changeScopeModalVisible: false,
			changeTypeModalVisible: false,
			newScope: null,
			newType: null,
			newAccount: null,
			newSecondaryAccount: null,
			implementedRules: [],
			virtualPrivateClouds: [],
			secondaryVirtualPrivateClouds: [],
			loading: false,
			subToInternetWarningVisible: true,
			loadingImplementedRules: true,
			failedImplementedRules: false,
			selectedFromExisting: [],
		};
		this.initFormModel();
	}

	async setImplementedRules(account: AWSAccount, secondaryAccount: AWSAccount) {
		let { selectedType, selectedScope, cartaId } = this.state.stateForm;
		let { awsAccount, secondaryAwsAccount } = this.state;
		let filters = { selectedType, selectedScope, cartaId, awsAccount, secondaryAwsAccount };

		if (!this.isExternalAccountsScope() && this.areAllFiltersCompleted(filters)) {
			try {
				const implementedRules =
					account != null && secondaryAccount != null
						? await this.getPortOpeningRulesForGllAWStoGllAWS(account, secondaryAccount, filters)
						: await this.getPortOpeningRules(account, filters);
				this.setState({
					implementedRules: implementedRules,
					loadingImplementedRules: false,
					failedImplementedRules: false,
				});
			} catch (error) {
				console.log(error);
				this.setState({
					failedImplementedRules: true,
				});
				this.handleEndModal(
					'Error while retrieving account info, please try again',
					'error',
					5000,
					this.returningRoute()
				);
			}
		}
	}

	filterAccountAssociatedPORRules(account: AWSAccount, condition: (obj) => boolean) {
		const portOpeningRules = account.awsPortOpeningRequests.filter(condition).flatMap((o) => o.awsPortOpeningRules);

		const groupedRules = portOpeningRules.reduce((r, a) => {
			r[a.awsName] = [...(r[a.awsName.indexOf[-1]] || []), a];
			return r;
		}, {});

		return Object.values(groupedRules)
			.filter((data: any) => data[0].operation?.name !== 'Remove')
			.map((a) => a[0]);
	}

	async getPortOpeningRulesForGllAWStoGllAWS(account: AWSAccount, secondaryAccount: AWSAccount, filters) {
		let { selectedType, selectedScope, cartaId } = filters;
		let request = await this.awsPortOpeningRequestService.getAWSPORRulesByFilters(
			account.account.id,
			cartaId[0]?.cartaAppId,
			selectedType,
			selectedScope,
			secondaryAccount.account.id
		);
		return request?.map(rule => new AWSPortOpeningRule(rule))
	}

	async getPortOpeningRules(account: AWSAccount, filters) {
		let { selectedType, selectedScope, cartaId } = filters;
		let request = await this.awsPortOpeningRequestService.getAWSPORRulesByFilters(
			account.account.id,
			cartaId[0]?.cartaAppId,
			selectedType,
			selectedScope
		);
		return request?.map(rule => new AWSPortOpeningRule(rule))
	}

	async componentDidMount() {
		window.scroll(0, 0);
		this.setState({ loading: true });
		if (this.props.draftRequest) {
			await this.loadPrimaryAccount(this.props.draftRequest?.accountId);
			if (this.props.draftRequest.direction.name == PortOpeningRequestsDirection.GLLAWSToGLLAWS.name)
				await this.loadSecondaryAccount(this.props.draftRequest?.secondaryAccountId);
			this.setState({ stateForm: this.stateFormFromRequest(this.props.draftRequest) });
			await this.handleAppEndorserOptions(this.props.draftRequest?.cartaId[0]?.cartaAppId);
		}
		if (this.props.location.state) {
			const { selectedRules }: ILocationState = this.props.location.state;
			this.handleStateFormChange('selectedScope', selectedRules[0].direction.name);
			this.handleStateFormChange(
				'ruleList',
				selectedRules.map((rule) => {
					return { ...rule, operation: RuleOperation.Modify };
				})
			);
			this.setState({
				selectedFromExisting: selectedRules.map((rule) => rule.name),
			});
		}
		this.setState({
			loading: false,
		});
	}

	async loadAccount(
		id: Number,
		stateKeys: { accountKey: string; vpcKey: string },
		formHandlerKey: string,
		formChangeKey: string
	) {
		try {
			const request =
				this.props.draftRequest.direction.name != PortOpeningRequestsDirection.ExternalAccounts.name
					? new AWSAccount(await this.getAWSAccount(id, false))
					: this.getExternalAccount(id);

			this.setState((prevState) => ({
				...prevState,
				[stateKeys.accountKey]: request,
				[stateKeys.vpcKey]: request.vpc,
			}));

			if (formHandlerKey === 'awsAccount') {
				this.setImplementedRules(request, null);
			}

			this.stateFormHandler()[formHandlerKey].onChange(request);
			this.handleStateFormChange(formChangeKey, request.account.id);
		} catch (error) {
			const HTTP_FORBIDDEN_ERROR_CODE = 403;
			if (error.status === HTTP_FORBIDDEN_ERROR_CODE) {
				this.handleEndModal(
					'You are not allowed to see this application.',
					'error',
					5000,
					this.returningRoute()
				);
			} else {
				this.handleEndModal(
					'The application data could not be retrieved.',
					'error',
					10000,
					this.returningRoute()
				);
			}
		}
	}

	async loadPrimaryAccount(id: Number) {
		await this.loadAccount(
			id,
			{ accountKey: 'newAccount', vpcKey: 'virtualPrivateClouds' },
			'awsAccount',
			'accountId'
		);
	}

	async loadSecondaryAccount(id: number) {
		await this.loadAccount(
			id,
			{ accountKey: 'newSecondaryAccount', vpcKey: 'secondaryVirtualPrivateClouds' },
			'secondaryAwsAccount',
			'secondaryAccountId'
		);
	}

	isRequestWithDOAG = () => !!this.props.draftRequest?.approvingUser;

	initFormModel() {
		super.initFormModel();

		this.formModel.addField('type', {
			getValue: () => this.state.stateForm.selectedType,
			validation: {
				required: true,
			},
		});

		this.formModel.addField('direction', {
			getValue: () => this.state.stateForm.selectedScope,
			validation: {
				required: true,
			},
		});

		this.formModel.fields.portOpeningRules.getValue = () =>
			this.state.stateForm.ruleList.map((rule) => new SubmitAWSPortOpeningRequestRule(rule));

		this.formModel.fields.secondaryAccountId.validation.required = () => this.isGLLAwstoGLLAwsScope();

		this.formModel.addField('AWSPortOpeningRequestId', {
			getValue: () => this.props.draftRequest?.id,
		});

		this.formModel.fields.approverId.validation.required = () => this.isRequestWithDOAG();

		this.formModel.fields.appEndorserId.validation.required = () => !this.isRequestWithDOAG();
	}

	stateFormFromRequest(por: AWSPortOpeningRequest): StateForm {
		let obj = {
			...super.stateFormFromRequest(por),
			selectedScope: por.direction?.name,
			selectedType: por.type?.name,
		};
		return {
			...obj,
			ruleList: obj.ruleList.map((rule) => {
				return { ...rule };
			}),
		};
	}

	returningRoute = () => {
		return `/awsPortOpeningRequest/connectedAWS`;
	};

	handleCancel = () => {
		this.setSubmissionAlertInProgress('Cancelling...');
		this.setState(
			{
				cancelModalVisible: false,
			},
			async () => {
				try {
					await this.awsPortOpeningRequestService.cancel(this.props.draftRequest?.id, {
						cancellationReason: 'Draft dismissed',
					});
					this.handleEndModal('Request cancelled sucessfully.', 'success', 5000, this.returningRoute());
				} catch (err) {
					this.setSubmissionAlertError(
						err?.body?.message ?? 'An error occured when processing your request. Please, try again later.'
					);
				} finally {
					this.setState({
						operationInProgress: false,
					});
				}
			}
		);
	};

	handleSubmit = async () => {
		this.setSubmissionAlertInProgress('Submitting your request...');
		if (this.isRequestWithDOAG()) {
			const isVerified = await this.approverLevelVerified();
			if (!isVerified) return;
		}
		try {
			const model = this.formModel.create(SubmitAWSPortOpeningRequest);
			const request = await this.awsPortOpeningRequestService.create(model);

			this.handleEndModal(
				`Your request was submitted successfully. You will receive a notification via email once the process has finished.`,
				'success',
				5000,
				this.returningRoute()
			);
		} catch (err) {
			this.setSubmissionAlertError(
				err?.response?.data.message ?? 'An error occured when processing your request. Please, try again later.'
			);
		} finally {
			this.setState({
				operationInProgress: false,
			});
		}
	};

	handleUpdate = async () => {
		this.setSubmissionAlertInProgress('Saving changes...');
		try {
			const model = this.formModel.create(SubmitAWSPortOpeningRequest);
			const request = await this.awsPortOpeningRequestService.saveDraft(model);

			this.handleEndModal(
				`Request #${'request.id'} was saved successfully.`,
				'success',
				5000,
				this.returningRoute()
			);
		} catch (err) {
			this.setSubmissionAlertError(
				err?.body?.message ?? 'An error occured when processing your request. Please, try again later.'
			);
		} finally {
			this.setState({
				operationInProgress: false,
			});
		}
	};

	handleTypeChange = (event: any) => {
		if (this.state.stateForm.ruleList.length <= 0) {
			this.handleStateFormChange('selectedScope', '');
			this.handleStateFormChange('cartaId', []);
			this.handleStateFormChange('appEndorser', '');
			this.cleanAppEndorserOptions();
			this.handleStateFormChange('selectedType', event.target.value);
			this.setState({ awsAccount: null });
			this.setState({ implementedRules: [] });
		} else {
			this.setState({ changeTypeModalVisible: true, newType: event.target.value });
		}
	};

	confirmTypeChange = () => {
		this.handleStateFormChange('selectedType', this.state.newType);
		this.handleStateFormChange('selectedScope', '');
		this.handleStateFormChange('ruleList', []);
		this.setState({
			changeTypeModalVisible: false,
			newType: null,
		});
	};

	handleScopeChange = (event: any) => {
		if (this.state.stateForm.ruleList.length <= 0) {
			this.handleStateFormChange('selectedScope', event.target.value);
		} else {
			this.setState({ changeScopeModalVisible: true, newScope: event.target.value });
		}
	};

	confirmScopeChange = () => {
		this.handleStateFormChange('selectedScope', this.state.newScope);
		this.handleStateFormChange('ruleList', []);
		this.setState({
			changeScopeModalVisible: false,
			newScope: null,
		});
	};

	handleAccountChange = (event: AWSAccount) => {
		if (this.state.stateForm.ruleList.length > 0 && !this.state.newAccount) {
			this.setState({ changeAccountModalVisible: true, newAccount: event });
		} else {
			this.stateFormHandler().awsAccount.onChange(event);

			if (!this.isGLLAwstoGLLAwsScope()) {
				this.setImplementedRules(event, null);
			} else if (this.state.secondaryAwsAccount != null)
				this.setImplementedRules(event, this.state.secondaryAwsAccount);

			this.setState({
				virtualPrivateClouds: event.vpc,
				newAccount: null,
			});
			this.handleStateFormChange('accountId', event.account.id);
			this.handleStateFormChange('delegatedApprover', null);
		}
	};

	confirmAccountChange = () => {
		this.stateFormHandler().awsAccount.onChange(this.state.newAccount);
		this.handleStateFormChange('accountId', this.state.newAccount.account.id);
		this.handleStateFormChange('ruleList', []);

		if (!this.isGLLAwstoGLLAwsScope()) {
			this.setImplementedRules(this.state.newAccount, null);
		} else if (this.state.secondaryAwsAccount != null)
			this.setImplementedRules(this.state.newAccount, this.state.secondaryAwsAccount);

		this.setState({
			changeAccountModalVisible: false,
			virtualPrivateClouds: this.state.newAccount.vpc,
			newAccount: null,
		});
	};

	handleSecondaryAccountChange = (event: AWSAccount, accountKey: string) => {
		if (this.state.stateForm.ruleList.length > 0 && !this.state.newSecondaryAccount) {
			this.setState({ changeAccountModalVisible: true, newSecondaryAccount: event });
		} else {
			this.stateFormHandler().secondaryAwsAccount.onChange(event);

			if (this.isGLLAwstoGLLAwsScope() && this.state.awsAccount != null)
				this.setImplementedRules(this.state.awsAccount, event);

			this.setState({
				secondaryVirtualPrivateClouds: event.vpc,
				newSecondaryAccount: null,
			});
			this.handleStateFormChange('secondaryAccountId', event.account.id);
		}
	};

	confirmSecondaryAccountChange = () => {
		this.stateFormHandler().awsAccount.onChange(this.state.newSecondaryAccount);
		this.handleStateFormChange('secondaryAccountId', this.state.newSecondaryAccount.account.id);
		this.handleStateFormChange('ruleList', []);

		if (this.isGLLAwstoGLLAwsScope() && this.state.awsAccount != null)
			this.setImplementedRules(this.state.awsAccount, this.state.newSecondaryAccount);

		this.setState({
			changeSecondaryAccountModalVisible: false,
			secondaryVirtualPrivateClouds: this.state.newSecondaryAccount.vpc,
			newSecondaryAccount: null,
		});
	};

	ruleIsAddedAlready(rule: AWSPortOpeningRule): boolean {
		return this.state.stateForm.ruleList.map((editedRule) => editedRule.awsName).includes(rule.awsName);
	}

	getImplementedRulesToShow(): AWSPortOpeningRule[] {
		return this.state.implementedRules.filter(
			(rule) => !this.ruleIsAddedAlready(rule) && !this.state.selectedFromExisting.includes(rule.name)
		);
	}

	setSelectedFromExisting = (rulesName: string[]) => {
		this.setState({
			selectedFromExisting: rulesName,
		});
	};

	isValid = () => {
		return this.formModel.isValid() && (this.props.draftRequest || !this.wasNotEdited());
	};

	wasNotEdited = () => {
		return this.state.stateForm.ruleList.some((rule) => rule.awsName != undefined);
	};

	isGLLAwstoGLLAwsScope() {
		return this.state.stateForm.selectedScope == PortOpeningRequestsDirection.GLLAWSToGLLAWS.name;
	}

	isExternalAccountsScope() {
		return this.state.stateForm.selectedScope == PortOpeningRequestsDirection.ExternalAccounts.name;
	}

	isEmptyScopeOrType() {
		return this.state.stateForm.selectedScope == '' || this.state.stateForm.selectedType == '';
	}

	areAllFiltersCompleted(filters) {
		return Boolean(
			filters.selectedType !== '' &&
				filters.selectedScope !== '' &&
				filters.awsAccount != null &&
				filters.cartaId.length > 0 &&
				(!this.isGLLAwstoGLLAwsScope() || filters.secondaryAwsAccount != null)
		);
	}

	handleExternalAccountChange = (event) => {
		const accountId = event.target.value;

		const account = this.getExternalAccount(accountId);

		this.setState({ awsAccount: account });
		this.handleAccountChange(account);
	};

	externalAccountsName() {
		return [{ text: 'SAPrise', value: 1 }];
	}

	awsExternalAccountInformation(accountId: Number) {
		const externalAccountInfo = {
			'1': {
				name: 'SAPrise',
			},
		};

		return accountId != undefined ? externalAccountInfo[accountId.toString()] : '';
	}

	getExternalAccount(accountId: Number) {
		const accountInfo = this.awsExternalAccountInformation(accountId);
		const account = new AWSAccount({
			account: {
				id: accountId,
				name: accountInfo.name,
			},
			owner: '00000000-0000-0000-0000-000000000000',
			vpc: [],
			awsPortOpeningRequests: [],
		});

		return account;
	}

	render() {
		return (
			<>
				<RequestFormWrapper
					title={'New AWS Port Opening Request'}
					loading={this.state.loading}
					linkTree={this.getLinkTree()}
				>
					<p>
						For more information about AWS PORs {/*TODO: Add link to wiki*/}
						<TextLink className="em-u-margin-right-none" external target="_blank" href="">
							Click here
						</TextLink>
					</p>
					{this.state.submissionAlert.visible && (
						<Alert
							variant={this.state.submissionAlert.variant}
							onClose={this.handleSubmissionAlertClose}
							body={this.state.submissionAlert.text}
						/>
					)}
					<Grid variant="halves">
						<Grid.Item>
							<AWSPORTypeInput
								scope={{
									value: this.state.stateForm.selectedType,
									validation: this.formModel.fields.type.validation,
									onChange: (event) => {
										this.handleTypeChange(event);
									},
									disabled: this.state.operationInProgress,
								}}
							/>
						</Grid.Item>
						<Grid.Item>
							<AWSPORScopeInput
								direction={{
									value: this.state.stateForm.selectedScope,
									validation: this.formModel.fields.direction.validation,
									onChange: (event) => this.handleScopeChange(event),
									disabled: this.state.operationInProgress,
								}}
								type={this.state.stateForm.selectedType}
							/>
						</Grid.Item>
					</Grid>
					<Grid variant="halves">
						<Grid.Item>
							<EnvironmentInput
								{...this.stateFormHandler().environment}
								disabled={this.state.operationInProgress}
							/>
						</Grid.Item>
					</Grid>
					{!this.isEmptyScopeOrType() ? (
						<>
							<Grid variant="halves">
								{this.isExternalAccountsScope() && (
									<Grid.Item>
										<SelectField
											label="AWS External Account"
											value={this.state.awsAccount?.account?.id}
											options={this.externalAccountsName()}
											className="full-width"
											onChange={this.handleExternalAccountChange}
										/>
									</Grid.Item>
								)}
								{!this.isExternalAccountsScope() && (
									<Grid.Item>
										<AWSAccountLookup
											{...this.stateFormHandler().awsAccount}
											onAccountChange={this.handleAccountChange}
											newAccount={this.state.newAccount}
											disabled={this.state.operationInProgress}
											label={
												this.isGLLAwstoGLLAwsScope()
													? "AWS Primary account's Name or Id (Source)"
													: undefined
											}
										/>
									</Grid.Item>
								)}
								{this.isGLLAwstoGLLAwsScope() ? (
									<Grid.Item>
										<AWSAccountLookup
											{...this.stateFormHandler().secondaryAwsAccount}
											onAccountChange={this.handleSecondaryAccountChange}
											newAccount={this.state.secondaryAwsAccount}
											disabled={this.state.operationInProgress}
											label={
												this.isGLLAwstoGLLAwsScope()
													? "AWS Secondary account's Name or Id (Destination)"
													: undefined
											}
										/>
									</Grid.Item>
								) : (
									<></>
								)}
							</Grid>
							<Grid variant="halves">
								<Grid.Item>
									<UserLookup
										label="Technical contact"
										{...this.stateFormHandler().contact}
										disabled={this.state.operationInProgress}
									/>
								</Grid.Item>
								{this.isRequestWithDOAG() &&
									<Grid.Item>
										<ApproverInput
											{...this.stateFormHandler().approver}
											disabled={this.state.operationInProgress}
											scope={this.state.stateForm.selectedScope}
										/>
									</Grid.Item>
								}
							</Grid>
							<Grid variant="halves">
								<Grid.Item>
									<CartaInput
										{...this.stateFormHandler().cartaId}
										oneOptionOnly={this.state.stateForm.cartaId.length > 0}
										hideNotes={this.state.operationInProgress}
										disabled={this.state.operationInProgress}
									/>
								</Grid.Item>
								{!this.isRequestWithDOAG() &&
									<Grid.Item>
										<SelectField
											label="Approver (Business Owner, IT Owner, IT Architect)"
											className="full-width"
											{...this.stateFormHandler().appEndorser}
											disabled={
												this.state.operationInProgress ||
												this.stateFormHandler().appEndorser.options.length == 0
											}
											isLoading={this.stateFormHandler().appEndorser.options.length == 0}
											note={
												this.stateFormHandler().appEndorser.options.length == 0 &&
												'First select the DBM'
											}
										/>
									</Grid.Item>
								}
							</Grid>
							<TextAreaField
								label="Business Justification"
								{...this.stateFormHandler().businessJustification}
								disabled={this.state.operationInProgress}
							/>
						</>
					) : (
						<></>
					)}
					<AWSRulesTable
						{...this.stateFormHandler().ruleset}
						currentWorkflowState={
							this.props.draftRequest?.workflowInstance.currentWorkflowInstanceState.workflowState.value
						}
						porDirection={new PortOpeningRequestsDirection().fromName(this.state.stateForm.selectedScope)}
						porType={new AWSPortOpeningRequestsTypes().fromName(this.state.stateForm.selectedType)}
						implementedRules={this.getImplementedRulesToShow()}
						readonly={this.state.operationInProgress}
						virtualPrivateClouds={this.state.virtualPrivateClouds}
						secondaryVirtualPrivateClouds={this.state.secondaryVirtualPrivateClouds}
						loadingImplementedRules={
							!this.isExternalAccountsScope() ? this.state.loadingImplementedRules : false
						}
						failedImplementedRules={this.state.failedImplementedRules}
						selectedFromExisting={this.state.selectedFromExisting}
						setSelectedFromExisting={this.setSelectedFromExisting}
					/>
					{this.state.awsAccount == null && (
						<TextAreaField
							label="Additional Comments"
							{...this.stateFormHandler().comments}
							disabled={this.state.operationInProgress}
						/>
					)}
					<Grid variant="4up">
						<Grid.Item>
							{!!this.props.draftRequest && this.props.draftRequest?.isCancellable() && (
								<Button
									variant="secondary"
									onClick={() => {
										this.setState({
											cancelModalVisible: true,
											operationInProgress: true,
										});
									}}
									disabled={this.state.operationInProgress}
								>
									Cancel Request
								</Button>
							)}
						</Grid.Item>
						<Grid.Item />
						<Grid.Item>
							<Button
								variant="primary"
								disabled={!this.formModel.isValid() || this.state.operationInProgress}
								isLoading={this.state.operationInProgress}
								onClick={this.handleUpdate}
								loadingVariant="secondary"
								name="Save & Close"
							/>
						</Grid.Item>
						<Grid.Item>
							<Button
								variant="primary"
								disabled={!this.isValid() || this.state.operationInProgress}
								isLoading={this.state.operationInProgress}
								onClick={this.handleSubmit}
								loadingVariant="secondary"
								name="Submit Request"
							/>
						</Grid.Item>
					</Grid>
				</RequestFormWrapper>
				<ConfirmModal
					visible={this.state.cancelModalVisible}
					title={`Cancel POR #${this.props.draftRequest?.id}`}
					question="Are you sure you want to cancel this request? This action cannot be undone."
					confirmButton={{
						label: 'Cancel Request',
						props: {
							variant: 'primary',
							color: 'negative',
						},
					}}
					onConfirm={this.handleCancel}
					onCancel={() => {
						this.setState({
							cancelModalVisible: false,
							operationInProgress: false,
						});
					}}
				/>
				<ConfirmModal
					visible={this.state.changeAccountModalVisible}
					title="Change Account"
					question="Are you sure you want to continue? If so, the ruleset will be cleared."
					confirmButton={{
						label: 'Yes',
					}}
					onConfirm={this.confirmAccountChange}
					onCancel={() => {
						this.setState({
							changeAccountModalVisible: false,
						});
					}}
				/>
				<ConfirmModal
					visible={this.state.changeSecondaryAccountModalVisible}
					title="Change Account"
					question="Are you sure you want to continue? If so, the ruleset will be cleared."
					confirmButton={{
						label: 'Yes',
					}}
					onConfirm={this.confirmSecondaryAccountChange}
					onCancel={() => {
						this.setState({
							changeSecondaryAccountModalVisible: false,
						});
					}}
				/>
				<ConfirmModal
					visible={this.state.changeScopeModalVisible}
					title="Change Scope"
					question="Are you sure you want to continue? If so, the ruleset will be cleared."
					confirmButton={{
						label: 'Yes',
					}}
					onConfirm={this.confirmScopeChange}
					onCancel={() => {
						this.setState({
							changeScopeModalVisible: false,
						});
					}}
				/>
				<ConfirmModal
					visible={this.state.changeTypeModalVisible}
					title="Change Type"
					question="Are you sure you want to continue? If so, the ruleset will be cleared."
					confirmButton={{
						label: 'Yes',
					}}
					onConfirm={this.confirmTypeChange}
					onCancel={() => {
						this.setState({
							changeTypeModalVisible: false,
						});
					}}
				/>
				<AlertModal {...this.state.endModal} willTimeout={false} onClose={this.endAndRedirect} />
			</>
		);
	}
}

const AWSPortOpeningRequestsCreate = (props: AWSPortOpeningRequestsCreateProps) => {
	return (
		<AWSPortOpeningRequestsCreateWithParams
			accountId={props.accountId}
			draftRequest={props.draftRequest}
			validNets={props.validNets}
			location={useLocation()}
			navigate={useNavigate()}
			params={useParams()}
		/>
	);
};

export default AWSPortOpeningRequestsCreate;
